import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { Dropdown, Layout, Menu, Pill, Button, Icon } from '@storable/canaveral-components';
import { Form } from 'react-bootstrap';
import Authorization from 'web/containers/Authorization';
import FacilityDataRefresh from 'admin/containers/FacilityDataRefresh';
import EchoInbox from 'admin/components/EchoInbox';
import ViewLiveButton from 'admin/components/ViewLiveButton';
import { BUILDER } from 'shared/abilities';
import { PSEUDOLOCALIZE, TRANSLATION_ID, TRANSLATION_TEXT } from 'store/modules/site';
import useFlipper from '../../../../modules/react/hooks/useFlipper';
import useCurrentUser from '../../../../admin/hooks/useCurrentUser';
import { FLIPPER_INTERNATIONALIZATION } from '../../../../constants/flippers';
import styles from './AdminControls.scss';
import gql from 'graphql-tag';

const { Header } = Layout;

const propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  facilityId: PropTypes.string,
  user: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }).isRequired,
  pageId: PropTypes.string.isRequired,
  blogEnabled: PropTypes.bool.isRequired,
  pathname: PropTypes.string.isRequired,
  translationTransform: PropTypes.oneOf([PSEUDOLOCALIZE, TRANSLATION_ID, TRANSLATION_TEXT]),
  setTransformPseudo: PropTypes.func.isRequired,
  setTransformTranslationId: PropTypes.func.isRequired,
  setTransformTranslationText: PropTypes.func.isRequired,
  sites: PropTypes.array,
  hideViewLiveButton: PropTypes.bool,
  siteHandlers: PropTypes.shape({
    identifier: PropTypes.string,
    hostname: PropTypes.string,
    handler: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.string,
    ]),
  }),
  currentSite: PropTypes.shape({
    id: PropTypes.string,
    identifier: PropTypes.string,
    primaryHostname: PropTypes.shape({
      hostname: PropTypes.string,
    }),
  }),
};

const defaultProps = {
  children: undefined,
  facilityId: undefined,
  hideViewLiveButton: undefined,
  siteHandlers: [],
  currentSite: undefined,
};

const SITE_SWITCHER = 'site-switcher';

const AdminControls = ({
  children,
  facilityId,
  translationTransform,
  setTransformPseudo,
  setTransformTranslationId,
  setTransformTranslationText,
  user: { name },
  hideViewLiveButton,
  siteHandlers,
  currentSite,
}) => {
  const initials = name.split(' ').map(part => part[0]).join('').toUpperCase();

  const i18nEnabled = useFlipper(FLIPPER_INTERNATIONALIZATION);

  const [dataRefreshShow, setDataRefreshShow] = useState(false);
  const [notificationsShow, setNotificationsShow] = useState(false);
  const [menuToShow, setMenuToShow] = useState();

  const { isHyper } = useCurrentUser();
  const userIsHyper = isHyper();

  const showDefaultMenu = !menuToShow;
  const hasMultipleWebsites = siteHandlers.length > 1;
  const menuHeaderEnabled = hasMultipleWebsites || i18nEnabled;

  const closeDataRefresh = () => {
    setDataRefreshShow(false);
    window.location.reload();
  };
  const openDataRefresh = () => setDataRefreshShow(true);

  const toggleNotifications = () => setNotificationsShow(!notificationsShow);

  const toggle = (
    <Dropdown.Toggle>
      <Pill context="subtle" diameter={31}>{initials}</Pill>
    </Dropdown.Toggle>
  );

  const switchWebsitesPanelHeader = (
    <div className={styles.menuItem}>
      <Button onClick={() => setMenuToShow(undefined)}>
        <Icon id="angle-left" className={styles.switcherMenuIconBack} />
        Switch Websites
      </Button>
    </div>
  );

  const i18nHeader = (
    <>
      <div>
        <label className={styles.label}>
          <Form.Check
            type="radio"
            name={TRANSLATION_TEXT}
            checked={translationTransform === TRANSLATION_TEXT}
            onChange={setTransformTranslationText}
            value={TRANSLATION_TEXT}
          />
          <span className={styles.text}>Translation</span>
        </label>
      </div>
      <div>
        <label className={styles.label}>
          <Form.Check
            type="radio"
            name={PSEUDOLOCALIZE}
            checked={translationTransform === PSEUDOLOCALIZE}
            onChange={setTransformPseudo}
            value={PSEUDOLOCALIZE}
          />
          <span className={styles.text}>Pseudolocalize</span>
        </label>
      </div>
      <div>
        <label className={styles.label}>
          <Form.Check
            type="radio"
            name={TRANSLATION_ID}
            checked={translationTransform === TRANSLATION_ID}
            onChange={setTransformTranslationId}
            value={TRANSLATION_ID}
          />
          <span className={styles.text}>Show Translation ID</span>
        </label>
      </div>
    </>
  );

  const getDefaultMenuHeader = () => (
    <Menu.Header>
      {hasMultipleWebsites && (
        <div className={styles.siteSwitcherMenuHeader}>
          {currentSite?.primaryHostname?.hostname.replace('www.', '') || currentSite.identifier}
        </div>
      )}
      {i18nEnabled && i18nHeader}
    </Menu.Header>
  );

  const getMenuHeader = (menu) => {
    if (menu === SITE_SWITCHER) {
      return switchWebsitesPanelHeader;
    }
    return getDefaultMenuHeader();
  };

  // This function uses hard links so the UI will refresh with the new company
  const getSwitcherItem = ({ handler, identifier, hostname }) => {
    const displayName = hostname?.replace('www.', '') || identifier;
    const itemStyle = !hostname && styles.identifier;
    if (identifier === currentSite.identifier) {
      return (
        <Menu.Item
          key={identifier}
          className={itemStyle}
          disabled
        >
          {displayName}
        </Menu.Item>
      );
    }

    return typeof handler === 'function'
      ? (
        <Menu.Item key={identifier} onClick={handler} className={itemStyle}>
          {displayName}
        </Menu.Item>
      ) : (
        <Menu.Item key={identifier} componentClass="a" href={handler} className={itemStyle}>
          {displayName}
        </Menu.Item>
      );
  };

  return (
    <React.Fragment>
      <Header.Group pull="right">
        {children}
        {!hideViewLiveButton && <ViewLiveButton />}
      </Header.Group>
      <Header.Group pull="right" bordered>
        <div className={styles.wrapper}>
          <Button
            size="sm"
            label="Help"
            context="bare"
            target="_blank"
            icon="tool-tip"
            componentClass="a"
            className={styles.button}
            rel="noopener noreferrer"
            href="https://storedge.zendesk.com/hc/en-us"
          />
        </div>
      </Header.Group>
      <Header.Group pull="right" bordered>
        <Authorization action="manage" subject={BUILDER}>
          {authorized => (
            <Dropdown
              pull="right"
              toggle={toggle}
              className={styles.dropdown}
            >
              <Menu header={authorized && menuHeaderEnabled ? getMenuHeader(menuToShow) : undefined}>
                {hasMultipleWebsites && showDefaultMenu && userIsHyper && (
                  <Menu.Item
                    onClick={() => setMenuToShow(SITE_SWITCHER)}
                    className={styles.menuItem}
                    keepMenuOpenWhenSelected
                  >
                    Switch Websites
                    <Icon className={styles.switcherMenuIcon} id="angle-right" />
                  </Menu.Item>
                )}
                {hasMultipleWebsites && menuToShow === SITE_SWITCHER && userIsHyper && (
                  siteHandlers.map(getSwitcherItem)
                )}
                {showDefaultMenu && userIsHyper && (
                  <Menu.Item componentClass={Link} to="/admin/hyper">Hyper</Menu.Item>
                )}
                {showDefaultMenu && facilityId && userIsHyper && (
                  <>
                    <Menu.Item onClick={openDataRefresh}>
                      Refresh Facility Data
                    </Menu.Item>
                    <FacilityDataRefresh
                      facilityId={facilityId}
                      onDismiss={closeDataRefresh}
                      show={dataRefreshShow}
                    />
                  </>
                )}
                {showDefaultMenu &&
                  <>
                    <Menu.Item onClick={toggleNotifications}>What's New</Menu.Item>
                    <EchoInbox onDismiss={toggleNotifications} show={notificationsShow} />
                    <Menu.Item componentClass="a" href="/api/auth/logout">Log Out</Menu.Item>
                  </>
                }
              </Menu>
            </Dropdown>
          )}
        </Authorization>
      </Header.Group>
    </React.Fragment>
  );
};

AdminControls.propTypes = propTypes;
AdminControls.defaultProps = defaultProps;

AdminControls.fragments = {
  sites: gql`
    fragment AdminControlsSites on Site {
      id
      identifier
      primaryHostname {
        hostname
      }
    }
  `,
};

export default AdminControls;
